import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useMountEffect } from '../../../hooks/useMountEffect';
import { useStagePlayer } from './useStagePlayer';
import { useStageCursor } from './useStageCursor';

/* Chapter 03 — the client window, told from both sides of the handoff.

   ACT 1 · YOU SHARE: a WhatsApp chat with your client — you send the portal
   link and the pin in green (read ticks and all), she replies that she is
   opening it.
   ACT 2 · SHE OPENS: the chat gives way to her portal. The pin you just sent
   fills in, the view unlocks.
   ACT 3 · SHE WATCHES, LIVE: confirmations tick into the guests tab; the
   cursor (her hand now) navigates to the venue tab where the seat map fills
   past capacity and one click trims the room back; then to invoices, where
   the balance is stamped paid.

   The floor caption narrates whose hands we are in. Same scene system as the
   rest: lit floor, 3D surfaces, spring cursor, offscreen parking, pause
   control, reduced-motion still. */

const PANEL_EASE = [0.23, 1, 0.32, 1] as const;

type Tab = 'guests' | 'venue' | 'invoices';
const TAB_ORDER: Tab[] = ['guests', 'venue', 'invoices'];

/* Cursor targets, px on the scene floor (portal card sits at left 18/top 24). */
const TARGETS = {
    venueTab: { x: 124, y: 87 },
    invoicesTab: { x: 192, y: 86 },
    trimChip: { x: 218, y: 288 },
    rest: { x: 354, y: 346 },
};

const GUEST_ROWS: { name: string; meta: string }[] = [
    { name: 'ngozi okafor +1', meta: 'confirmed' },
    { name: 'chief & mrs. adeyemi', meta: 'confirmed' },
    { name: 'halima sadiq', meta: 'confirmed' },
];

const CAPACITY = 300;
const SEATS = 50; /* one dot ≈ six seats */
const GUEST_BASE = 259;
const PIN = '4482';

const seatFill = (count: number) =>
    Math.min(SEATS, Math.round((count / CAPACITY) * SEATS));
const overflowDots = (count: number) =>
    Math.max(0, Math.min(7, Math.round((count - CAPACITY) / 6)));

type ShareBubble = { id: number; who: 'planner' | 'client'; body: React.ReactNode };

const ClientWindowStage: React.FC = () => {
    const reduced = useReducedMotion();
    const player = useStagePlayer();
    const cursor = useStageCursor(TARGETS.rest.x, TARGETS.rest.y);

    /* Reduced motion sees the money shot: the venue tab, trimmed and calm. */
    const [mode, setMode] = useState<'share' | 'portal'>(reduced ? 'portal' : 'share');
    const [shareShown, setShareShown] = useState(0);
    const [locked, setLocked] = useState(!reduced);
    const [pinFill, setPinFill] = useState(reduced ? 4 : 0);
    const [tab, setTab] = useState<Tab>(reduced ? 'venue' : 'guests');
    const [direction, setDirection] = useState(1);
    const [hover, setHover] = useState<string | null>(null);
    const [guestsShown, setGuestsShown] = useState(reduced ? GUEST_ROWS.length : 0);
    const [venueCount, setVenueCount] = useState(reduced ? 296 : 262);
    const [trimmed, setTrimmed] = useState(reduced);
    const [invoiceStep, setInvoiceStep] = useState(reduced ? 2 : 0);

    const goTab = (next: Tab) => {
        setDirection(TAB_ORDER.indexOf(next) > TAB_ORDER.indexOf(tab) ? 1 : -1);
        setTab(next);
    };

    useMountEffect(() => {
        if (reduced) return undefined;
        return player.run(async (h) => {
            while (h.alive()) {
                await h.gate();

                /* ── reset ── */
                setMode('share');
                setShareShown(0);
                setLocked(true);
                setPinFill(0);
                setTab('guests');
                setDirection(1);
                setGuestsShown(0);
                setVenueCount(262);
                setTrimmed(false);
                setInvoiceStep(0);
                setHover(null);

                /* ── act 1: you share the link and pin ── */
                await h.wait(750);
                setShareShown(1);
                await h.wait(1200);
                if (!h.alive()) return;
                setShareShown(2);
                await h.wait(1000);
                if (!h.alive()) return;
                setShareShown(3);
                await h.wait(1250);
                if (!h.alive()) return;

                /* ── act 2: she opens it ── */
                setMode('portal');
                await h.wait(850);
                for (let i = 1; i <= 4; i += 1) {
                    if (!h.alive()) return;
                    setPinFill(i);
                    await h.wait(160);
                }
                await h.wait(340);
                setLocked(false);
                await h.wait(650);

                /* ── act 3: the live view ── */
                for (let i = 1; i <= GUEST_ROWS.length; i += 1) {
                    if (!h.alive()) return;
                    setGuestsShown(i);
                    await h.wait(620);
                }
                await h.wait(750);

                await cursor.moveTo(TARGETS.venueTab.x, TARGETS.venueTab.y);
                if (!h.alive()) return;
                setHover('venue');
                await h.wait(210);
                await cursor.click(h.wait);
                goTab('venue');
                setHover(null);

                await h.wait(650);
                for (const count of [281, 313, 341]) {
                    if (!h.alive()) return;
                    setVenueCount(count);
                    await h.wait(800);
                }
                await h.wait(500);

                await cursor.moveTo(TARGETS.trimChip.x, TARGETS.trimChip.y);
                if (!h.alive()) return;
                setHover('trim');
                await h.wait(240);
                await cursor.click(h.wait);
                setTrimmed(true);
                setVenueCount(296);
                setHover(null);
                await h.wait(1100);

                await cursor.moveTo(TARGETS.invoicesTab.x, TARGETS.invoicesTab.y);
                if (!h.alive()) return;
                setHover('invoices');
                await h.wait(190);
                await cursor.click(h.wait);
                goTab('invoices');
                setHover(null);

                await h.wait(550);
                setInvoiceStep(1);
                await h.wait(850);
                setInvoiceStep(2);
                await h.wait(700);
                void cursor.moveTo(TARGETS.rest.x, TARGETS.rest.y);

                await h.wait(3000);
            }
        });
    });

    const guestCount = GUEST_BASE + guestsShown;
    const over = venueCount > CAPACITY;
    const filled = seatFill(venueCount);
    const spill = overflowDots(venueCount);

    const SHARE_BUBBLES: ShareBubble[] = [
        {
            id: 1,
            who: 'planner',
            body: (
                <>
                    here’s your event portal
                    <span
                        style={{
                            display: 'block',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                            color: '#0b6bcb',
                            marginTop: 2,
                        }}
                    >
                        comtev.ng/l/adaeze-emeka
                    </span>
                </>
            ),
        },
        { id: 2, who: 'planner', body: <>your pin: {PIN}</> },
        { id: 3, who: 'client', body: <>thank you! opening it now</> },
    ];

    return (
        <motion.div
            className="ngl-scene"
            style={{ minHeight: 384 }}
            {...player.viewportProps}
            role="img"
            aria-label="The planner shares the event portal on WhatsApp: a link and a four-digit pin, sent to their client adaeze, who replies that she is opening it. Her portal then unlocks with that pin onto a live view: confirmations tick into the guests tab, the venue seat map fills past its 300-seat capacity until a click on trim-to-fit settles it back to 296, and the invoices tab shows the balance stamped paid and withdrawn."
        >
            <AnimatePresence mode="wait">
                {mode === 'share' ? (
                    <motion.div
                        key="share"
                        className="ngl-scene-card ngl-scene-chat"
                        style={{ left: '14%', top: 44, width: '58%', maxWidth: 240 }}
                        initial={
                            reduced
                                ? false
                                : {
                                      opacity: 0,
                                      y: 40,
                                      rotateY: 14,
                                      scale: 0.92,
                                      filter: 'blur(3px)',
                                  }
                        }
                        animate={{
                            opacity: 1,
                            y: 0,
                            rotateY: 7,
                            rotateX: 2,
                            scale: 1,
                            filter: 'blur(0px)',
                        }}
                        exit={{
                            opacity: 0,
                            x: -70,
                            rotateY: 24,
                            scale: 0.9,
                            filter: 'blur(3px)',
                        }}
                        transition={{ duration: 0.55, ease: PANEL_EASE }}
                    >
                        <div className="ngl-scene-chat-head">
                            <span className="ngl-scene-avatar" aria-hidden="true">
                                A
                            </span>
                            <span>
                                <span
                                    className="ngl-scene-chat-name"
                                    style={{ display: 'block' }}
                                >
                                    adaeze
                                </span>
                                <span className="ngl-scene-chat-status">
                                    your client · online
                                </span>
                            </span>
                        </div>
                        <div className="ngl-scene-chat-feed">
                            <AnimatePresence initial={false}>
                                {SHARE_BUBBLES.slice(0, shareShown).map((bubble) => (
                                    <motion.div
                                        key={bubble.id}
                                        className={`ngl-ch-bubble ${
                                            bubble.who === 'planner'
                                                ? 'is-user'
                                                : 'is-bot'
                                        }`}
                                        initial={
                                            reduced
                                                ? false
                                                : { opacity: 0, y: 12, scale: 0.97 }
                                        }
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={
                                            reduced ? undefined : { opacity: 0, y: -10 }
                                        }
                                        transition={{ duration: 0.3, ease: PANEL_EASE }}
                                    >
                                        {bubble.body}
                                        {bubble.who === 'planner' && (
                                            <span
                                                className="ngl-wa-ticks"
                                                aria-hidden="true"
                                            >
                                                ✓✓
                                            </span>
                                        )}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key="portal"
                        className="ngl-scene-card"
                        style={{ left: 18, right: 18, top: 24, overflow: 'hidden' }}
                        initial={
                            reduced
                                ? false
                                : {
                                      opacity: 0,
                                      x: 70,
                                      rotateY: -18,
                                      scale: 0.94,
                                      filter: 'blur(3px)',
                                  }
                        }
                        animate={{
                            opacity: 1,
                            x: 0,
                            rotateY: -6,
                            rotateX: 2,
                            scale: 1,
                            filter: 'blur(0px)',
                        }}
                        transition={{ duration: 0.6, ease: PANEL_EASE }}
                    >
                        <div className="ngl-st-head">
                            <span className="ngl-st-title">adaeze’s view</span>
                            <span className={`ngl-st-tag${locked ? '' : ' is-live'}`}>
                                {locked ? `pin · ${PIN}` : 'live'}
                            </span>
                        </div>

                        <AnimatePresence mode="wait" initial={false}>
                            {locked ? (
                                <motion.div
                                    key="pin"
                                    className="ngl-pt-panel"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    exit={{ opacity: 0, y: -14, filter: 'blur(2px)' }}
                                    transition={{ duration: 0.4, ease: PANEL_EASE }}
                                >
                                    <span className="ngl-pt-pin" aria-hidden="true">
                                        {[1, 2, 3, 4].map((dot) => (
                                            <i
                                                key={dot}
                                                className={
                                                    pinFill >= dot
                                                        ? 'is-filled'
                                                        : undefined
                                                }
                                            />
                                        ))}
                                    </span>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="view"
                                    initial={{ opacity: 0, y: 22, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ duration: 0.55, ease: PANEL_EASE }}
                                >
                                    <nav className="ngl-pt-tabs" aria-hidden="true">
                                        {TAB_ORDER.map((t) => (
                                            <span
                                                key={t}
                                                className={`ngl-pt-tab${
                                                    tab === t ? ' is-active' : ''
                                                }${hover === t ? ' is-hover' : ''}`}
                                            >
                                                {t}
                                                {tab === t && (
                                                    <motion.span
                                                        layoutId="ngl-pt-underline"
                                                        className="ngl-pt-underline"
                                                        transition={{
                                                            duration: 0.35,
                                                            ease: PANEL_EASE,
                                                        }}
                                                    />
                                                )}
                                            </span>
                                        ))}
                                    </nav>

                                    <div className="ngl-pt-panel">
                                        <AnimatePresence
                                            mode="wait"
                                            custom={direction}
                                            initial={false}
                                        >
                                            {tab === 'guests' && (
                                                <motion.div
                                                    key="guests"
                                                    custom={direction}
                                                    initial={{
                                                        opacity: 0,
                                                        x: 36 * direction,
                                                    }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{
                                                        opacity: 0,
                                                        x: -30 * direction,
                                                        filter: 'blur(2px)',
                                                    }}
                                                    transition={{
                                                        duration: 0.4,
                                                        ease: PANEL_EASE,
                                                    }}
                                                >
                                                    <div className="ngl-st-row">
                                                        <span
                                                            className="ngl-st-name"
                                                            style={{
                                                                fontStyle: 'normal',
                                                            }}
                                                        >
                                                            <motion.span
                                                                key={guestCount}
                                                                style={{
                                                                    display:
                                                                        'inline-block',
                                                                }}
                                                                initial={{
                                                                    y: -8,
                                                                    opacity: 0,
                                                                }}
                                                                animate={{
                                                                    y: 0,
                                                                    opacity: 1,
                                                                }}
                                                                transition={{
                                                                    duration: 0.28,
                                                                    ease: PANEL_EASE,
                                                                }}
                                                            >
                                                                {guestCount}
                                                            </motion.span>{' '}
                                                            confirmed
                                                        </span>
                                                        <span className="ngl-st-meta">
                                                            updating live
                                                        </span>
                                                    </div>
                                                    <AnimatePresence>
                                                        {GUEST_ROWS.slice(
                                                            0,
                                                            guestsShown,
                                                        ).map((row) => (
                                                            <motion.div
                                                                key={row.name}
                                                                className="ngl-st-row"
                                                                initial={{
                                                                    opacity: 0,
                                                                    y: 10,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    y: 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.3,
                                                                    ease: PANEL_EASE,
                                                                }}
                                                            >
                                                                <span className="ngl-st-name">
                                                                    {row.name}
                                                                </span>
                                                                <span className="ngl-st-meta ngl-st-ok">
                                                                    {row.meta}
                                                                </span>
                                                            </motion.div>
                                                        ))}
                                                    </AnimatePresence>
                                                </motion.div>
                                            )}

                                            {tab === 'venue' && (
                                                <motion.div
                                                    key="venue"
                                                    custom={direction}
                                                    initial={{
                                                        opacity: 0,
                                                        x: 36 * direction,
                                                    }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{
                                                        opacity: 0,
                                                        x: -30 * direction,
                                                        filter: 'blur(2px)',
                                                    }}
                                                    transition={{
                                                        duration: 0.4,
                                                        ease: PANEL_EASE,
                                                    }}
                                                >
                                                    <div className="ngl-st-row">
                                                        <span
                                                            className="ngl-st-name"
                                                            style={{
                                                                fontStyle: 'normal',
                                                            }}
                                                        >
                                                            <motion.span
                                                                key={venueCount}
                                                                style={{
                                                                    display:
                                                                        'inline-block',
                                                                }}
                                                                initial={{
                                                                    y: -8,
                                                                    opacity: 0,
                                                                }}
                                                                animate={{
                                                                    y: 0,
                                                                    opacity: 1,
                                                                }}
                                                                transition={{
                                                                    duration: 0.28,
                                                                    ease: PANEL_EASE,
                                                                }}
                                                            >
                                                                {venueCount}
                                                            </motion.span>{' '}
                                                            of {CAPACITY}
                                                        </span>
                                                        <span
                                                            className={`ngl-st-meta${
                                                                over
                                                                    ? ' ngl-st-no'
                                                                    : ''
                                                            }`}
                                                        >
                                                            {over
                                                                ? 'tight · trim?'
                                                                : trimmed
                                                                  ? 'comfortable'
                                                                  : 'seat map'}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="ngl-pt-room"
                                                        aria-hidden="true"
                                                        style={{ marginTop: 10 }}
                                                    >
                                                        {Array.from(
                                                            { length: SEATS },
                                                            (_, i) => (
                                                                <span
                                                                    key={i}
                                                                    className={`ngl-pt-seat${
                                                                        i < filled
                                                                            ? ' is-filled'
                                                                            : ''
                                                                    }`}
                                                                    style={{
                                                                        transitionDelay: `${
                                                                            (i % 10) *
                                                                            14
                                                                        }ms`,
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                    </div>
                                                    <div
                                                        className="ngl-pt-overflow"
                                                        aria-hidden="true"
                                                    >
                                                        <AnimatePresence>
                                                            {Array.from(
                                                                { length: spill },
                                                                (_, i) => (
                                                                    <motion.i
                                                                        key={i}
                                                                        initial={{
                                                                            opacity: 0,
                                                                            scale: 0.5,
                                                                        }}
                                                                        animate={{
                                                                            opacity: 1,
                                                                            scale: 1,
                                                                        }}
                                                                        exit={{
                                                                            opacity: 0,
                                                                            scale: 0.5,
                                                                        }}
                                                                        transition={{
                                                                            duration: 0.25,
                                                                            delay:
                                                                                i * 0.05,
                                                                        }}
                                                                    />
                                                                ),
                                                            )}
                                                        </AnimatePresence>
                                                        {over && (
                                                            <motion.span
                                                                className="ngl-st-meta ngl-st-no"
                                                                initial={{ opacity: 0 }}
                                                                animate={{ opacity: 1 }}
                                                                style={{
                                                                    marginLeft: 4,
                                                                }}
                                                            >
                                                                outside the room
                                                            </motion.span>
                                                        )}
                                                    </div>
                                                    <div style={{ marginTop: 10 }}>
                                                        {(over || trimmed) && (
                                                            <motion.span
                                                                className={`ngl-pt-chip${
                                                                    hover === 'trim'
                                                                        ? ' is-hover'
                                                                        : ''
                                                                }${
                                                                    hover === 'trim' &&
                                                                    cursor.press
                                                                        ? ' is-press'
                                                                        : ''
                                                                }`}
                                                                initial={{
                                                                    opacity: 0,
                                                                    y: 8,
                                                                }}
                                                                animate={{
                                                                    opacity: 1,
                                                                    y: 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.3,
                                                                    ease: PANEL_EASE,
                                                                }}
                                                            >
                                                                {trimmed
                                                                    ? 'trimmed to fit ✓'
                                                                    : 'trim to fit'}
                                                            </motion.span>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}

                                            {tab === 'invoices' && (
                                                <motion.div
                                                    key="invoices"
                                                    custom={direction}
                                                    initial={{
                                                        opacity: 0,
                                                        x: 36 * direction,
                                                    }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{
                                                        opacity: 0,
                                                        x: -30 * direction,
                                                        filter: 'blur(2px)',
                                                    }}
                                                    transition={{
                                                        duration: 0.4,
                                                        ease: PANEL_EASE,
                                                    }}
                                                >
                                                    <div className="ngl-st-row">
                                                        <span
                                                            className="ngl-st-name"
                                                            style={{
                                                                fontStyle: 'normal',
                                                            }}
                                                        >
                                                            balance due
                                                        </span>
                                                        {invoiceStep >= 1 ? (
                                                            <motion.span
                                                                className="ngl-st-stamp"
                                                                initial={{
                                                                    scale: 1.6,
                                                                    opacity: 0,
                                                                    rotate: -14,
                                                                }}
                                                                animate={{
                                                                    scale: 1,
                                                                    opacity: 1,
                                                                    rotate: -6,
                                                                }}
                                                                transition={{
                                                                    duration: 0.3,
                                                                    ease: PANEL_EASE,
                                                                }}
                                                            >
                                                                paid
                                                            </motion.span>
                                                        ) : (
                                                            <span className="ngl-st-meta">
                                                                awaiting
                                                            </span>
                                                        )}
                                                    </div>
                                                    {invoiceStep >= 2 && (
                                                        <motion.div
                                                            className="ngl-st-row"
                                                            initial={{
                                                                opacity: 0,
                                                                y: 10,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                                y: 0,
                                                            }}
                                                            transition={{
                                                                duration: 0.3,
                                                                ease: PANEL_EASE,
                                                            }}
                                                        >
                                                            <span
                                                                className="ngl-st-name"
                                                                style={{
                                                                    fontStyle: 'normal',
                                                                }}
                                                            >
                                                                withdrawn to your bank
                                                            </span>
                                                            <span className="ngl-st-meta ngl-st-ok">
                                                                landed
                                                            </span>
                                                        </motion.div>
                                                    )}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>

            <span className="ngl-scene-caption" aria-hidden="true">
                {mode === 'share'
                    ? 'you share the link and pin'
                    : 'your client watches, live'}
            </span>

            {!reduced && mode === 'portal' && (
                <motion.div
                    className="ngl-cursor"
                    style={{ x: cursor.x, y: cursor.y }}
                    aria-hidden="true"
                >
                    <span
                        className={`ngl-cursor-dot${
                            cursor.press ? ' is-press' : ''
                        }`}
                    />
                    {cursor.press && (
                        <span key={cursor.ripple} className="ngl-cursor-ripple" />
                    )}
                </motion.div>
            )}

            {!reduced && (
                <button
                    type="button"
                    className="ngl-stage-pause"
                    aria-label={player.paused ? 'Play animation' : 'Pause animation'}
                    onClick={player.togglePaused}
                >
                    {player.paused ? (
                        <svg viewBox="0 0 10 10">
                            <path d="M2 1l7 4-7 4z" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 10 10">
                            <path d="M2 1h2.4v8H2zM5.6 1H8v8H5.6z" />
                        </svg>
                    )}
                </button>
            )}
        </motion.div>
    );
};

export default ClientWindowStage;
